import {
  AggregateResultRequest,
  ChartMetadata,
  GetAggregateResultQuery,
  GetAggregateResultQueryVariables,
  ResultAction,
} from '../../../../API';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../../shared/services/api.service';
import { AppService } from '../../../shared/services/app.service';
import { ChartDataService } from '../../services/chart-data.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { distinctUntilChanged, filter, map, Subject, takeUntil, tap } from 'rxjs';
import {
  GetChartQuery,
  GetChartQueryVariables,
  GetQueryRecordQuery,
  GetQueryRecordQueryVariables,
  Engagements,
} from '../../../../API';
import { GetAggregateResult, GetChart, GetQueryRecord } from '../../../../graphql/queries';

@Component({
  selector: 'app-build-chart',
  templateUrl: './build-chart.component.html',
  styleUrl: './build-chart.component.scss',
})
export class BuildChartComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  stepper: number = 0;
  isLoadingChart: boolean = false;
  engagement: string = '';
  private chartIdProcessed: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private appService: AppService,
    private chartDataService: ChartDataService,
    private nzMessageService: NzMessageService,
  ) {}

  ngOnInit(): void {
    this.appService.activeEngagement$
      .pipe(
        takeUntil(this.destroy$),
        map((engagement: Engagements) => engagement.engagement),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        filter(() => !this.chartIdProcessed),
        tap((engagement) => {
          this.engagement = engagement;
          this.processChartId();
          this.chartIdProcessed = true;
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.chartDataService.chartMetadata = null;
    this.chartDataService.setData([]);
    this.chartDataService.setType('{}');
  }

  private processChartId(): void {
    const chartId = this.route.snapshot.paramMap.get('chartId');
    if (chartId) {
      this.chartIdProcessed = true;
      this.stepper = 1;
      this.loadChartForEdit(chartId);
    }
  }

  private loadChartForEdit(chartId: string): void {
    this.isLoadingChart = true;
    this.apiService
      .query<GetChartQuery, GetChartQueryVariables>(GetChart, {
        chartId: chartId,
        engagementId: this.engagement,
      })
      .then((response) => {
        const chartData = response.data?.GetChart as ChartMetadata;
        this.chartDataService.chartMetadata = chartData;
        return Promise.all([this.getQueryData(chartData), this.getAggregateResults(chartData)]);
      })
      .then(([queryResult, aggregateResult]) => {
        const queryRecord = queryResult.data?.GetQueryRecord;
        queryRecord?.metadata && this.chartDataService.setType(queryRecord.metadata);

        const resultData = aggregateResult.data?.GetAggregateResult;
        if (resultData?.rows) {
          const parsedResultData = JSON.parse(resultData.rows);
          this.chartDataService.setData(parsedResultData);
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to load chart for edit ${error}`);
      })
      .finally(() => (this.isLoadingChart = false));
  }

  getQueryData(chartData: ChartMetadata) {
    return this.apiService.query<GetQueryRecordQuery, GetQueryRecordQueryVariables>(GetQueryRecord, {
      queryId: chartData.queryId!,
      engagementId: this.engagement,
    });
  }

  getAggregateResults(chartData: ChartMetadata) {
    const { queryId, engagement, athenaExecutionId } = chartData;
    const aggregateQueryRequest: AggregateResultRequest = {
      queryId: queryId!,
      engagement,
      athenaExecutionId: athenaExecutionId!,
      action: ResultAction.GET_AGGREGATE_RESULT,
    };
    return this.apiService.query<GetAggregateResultQuery, GetAggregateResultQueryVariables>(GetAggregateResult, {
      resultRequest: aggregateQueryRequest,
    });
  }

  goToNextStep(): void {
    // TODO: Add Validation for next step
    this.stepper = 1;
  }

  back(): void {
    this.stepper = 0;
  }
}
